import {
    type GetInviteResponse,
    JoinCompanyParams,
    PASSWORD_MIN_LENGTH,
    PASSWORD_REQUIREMENT_HINT,
    Role,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { alert } from "../../elements/alert-dialog";
import "../../elements/forgot-password-dialog";
import type { ForgotPasswordDialog } from "../../elements/forgot-password-dialog";
import { PUBLIC_PTC_MANAGE_URL, PUBLIC_PTC_STAFF_URL } from "../../env";
import { app } from "../../init";
import { validateNewPasswordInputs } from "../../lib/password-input";
import { singleton } from "../../lib/singleton";
import { routeProperty, Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";

@customElement("ptc-join-company")
export class JoinCompanyView extends StateMixin(Routing(LitElement)) {
    routePattern = /join\/(?<id>[^/]+)?/;

    @property()
    scope: "manage" | "staff" = "manage";

    @query("form")
    _form: HTMLFormElement;

    @query("input[name='password']")
    private _passwordInput: HTMLInputElement;

    @query("input[name='repeatPassword']")
    private _repeatPasswordInput: HTMLInputElement;

    @singleton("ptc-forgot-password-dialog")
    private _forgotPasswordDialog: ForgotPasswordDialog;

    @routeProperty({ arg: "id" })
    private _inviteId: string;

    @state()
    private _loading = false;

    @state()
    private _inviteInfo: GetInviteResponse;

    @state()
    private _page: "submit" | "success" = "submit";

    handleRoute() {
        if (!this._inviteId) {
            return;
        }
        // load in the background
        void this._load();
    }

    private async _load() {
        this._loading = true;
        try {
            this._inviteInfo = await app.api.getInvite(this._inviteId);
            this._page = "submit";
        } catch (e) {
            await alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _submit(e: FocusEvent) {
        e.preventDefault();

        this._loading = true;

        const password = new FormData(this._form).get("password") as string;

        try {
            await app.api.joinCompany(new JoinCompanyParams({ inviteId: this._inviteId, password, scope: this.scope }));
            this._page = "success";
        } catch (e) {
            await alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _forgotPassword() {
        void this._forgotPasswordDialog.show({
            email: this._inviteInfo?.profile.email,
            scope: this.scope,
        });
    }

    private _checkPasswordInputs() {
        // For an existing account the password is a login credential, not a new password —
        // the policy only applies when the invite creates the account.
        if (!this._inviteInfo || this._inviteInfo.hasAccount) {
            this._passwordInput.setCustomValidity("");
            this._repeatPasswordInput.setCustomValidity("");
            return;
        }
        const { profile, company } = this._inviteInfo;
        validateNewPasswordInputs(this._passwordInput, this._repeatPasswordInput, {
            email: profile.email,
            firstName: profile.firstName,
            lastName: profile.lastName,
            companyName: company.name,
        });
    }

    static styles = [
        shared,
        css`
            :host {
                z-index: 100;
                background: #fafafa;
            }

            .logo {
                width: 16em;
            }

            .wrapper {
                width: 100%;
                max-width: 26em;
                padding: 1em;
                box-sizing: border-box;
            }

            a {
                text-decoration: none;
            }

            .forgot-password {
                margin: 0.5em 0 -0.5em 0;
                padding: 0.4em;
            }
        `,
    ];

    render() {
        if (!this._inviteInfo) {
            return;
        }

        const {
            hasAccount,
            profile,
            inviter,
            company,
            invite,
            profile: { email },
        } = this._inviteInfo;

        return html`
            <div class="fullbleed scrolling">
                <div class="fill vertical centering layout">
                    <div class="wrapper" ?hidden=${this._page !== "submit"}>
                        <div class="centering layout">
                            <ptc-ui-logo variant="full" class="top-margined logo"></ptc-ui-logo>
                        </div>

                        <h1 class="text-centering">${hasAccount ? "Unternehmen Beitreten" : "Zugang Einrichten"}</h1>

                        <div class="double-margined text-centering">
                            Hallo <strong>${profile.firstName}</strong>! ${inviter.name} hat Sie eingeladen, Ihren
                            Mitarbeiter-Zugang für das Unternehmen <strong>${company.name}</strong> einzurichten!
                        </div>

                        ${invite.message
                            ? html`
                                  <div class="double-margined blue padded text-centering box">
                                      <strong>Nachricht:</strong> "${invite.message}"
                                  </div>
                              `
                            : ""}

                        <div class="card double-padded vertical layout">
                            <form class="vertical layout" @input=${this._checkPasswordInputs} @submit=${this._submit}>
                                <div class="subtle text-centering">
                                    ${!hasAccount
                                        ? "Bitte wählen Sie Ihr Passwort:"
                                        : "Bitte melden Sie sich mit Ihrem Passwort an:"}
                                </div>

                                <div class="field">
                                    <label>Emailadresse</label>
                                    <input type="email" required readonly .value=${email || ""} />
                                </div>

                                <div class="field">
                                    <label>Passwort</label>
                                    <input
                                        type="password"
                                        name="password"
                                        placeholder="Passwort"
                                        required
                                        minlength=${ifDefined(hasAccount ? undefined : PASSWORD_MIN_LENGTH)}
                                    />
                                    <div class="small subtle" ?hidden=${hasAccount}>${PASSWORD_REQUIREMENT_HINT}</div>
                                </div>

                                <div class="field" ?hidden=${hasAccount}>
                                    <label>Passwort Wiederholen</label>
                                    <input
                                        type="password"
                                        name="repeatPassword"
                                        placeholder="Passwort Wiederholen"
                                        ?required=${!hasAccount}
                                    />
                                </div>

                                <button type="submit" class="primary">
                                    ${hasAccount ? "Unternehmen Beitreten" : "Zugang Einrichten"}
                                </button>
                            </form>
                        </div>

                        ${hasAccount
                            ? html`
                                  <div class="centering layout">
                                      <button
                                          type="button"
                                          class="small transparent forgot-password"
                                          @click=${this._forgotPassword}
                                      >
                                          Passwort Vergessen?
                                      </button>
                                  </div>
                              `
                            : ""}
                    </div>

                    <div class="wrapper" ?hidden=${this._page !== "success"}>
                        <div class="double-margined centering layout">
                            <ptc-ui-logo variant="full" class="top-margined logo"></ptc-ui-logo>
                        </div>

                        <div class="margined huge text-centering green colored-text">
                            <ptc-ui-icon name="icon.status.success"></ptc-ui-icon> Zugang Eingerichtet
                        </div>

                        <div class="double-margined text-centering">
                            Du hast deinen Mitarbeiter-Zugang für
                            <strong>${this._inviteInfo.company.name}</strong> erfolgreich eingerichtet! Möchtest du dich
                            jetzt einloggen?
                        </div>

                        <div class="double-margined spacing vertical layout">
                            <a
                                href="${PUBLIC_PTC_STAFF_URL}?email=${encodeURIComponent(email)}"
                                class="big text-centering padded blue box"
                            >
                                <ptc-ui-icon name="icon.object.employee-group"></ptc-ui-icon> Zur Mitarbeiter-App
                                <ptc-ui-icon name="icon.navigation.forward" class="smaller"></ptc-ui-icon>
                            </a>
                            <a
                                href="${PUBLIC_PTC_MANAGE_URL}?email=${encodeURIComponent(email)}"
                                class="big padded text-centering orange box"
                                ?hidden=${this._inviteInfo.profile.role > Role.Manager}
                            >
                                <ptc-ui-icon name="icon.role.owner"></ptc-ui-icon> Zum Verwaltungszugang
                                <ptc-ui-icon name="icon.navigation.forward" class="smaller"></ptc-ui-icon>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
